import { useMemo, useState } from 'react';
import { Platform, type ViewStyle } from 'react-native';
import { AnimatedCard } from '../../../components/animations';
import { Sparkline } from '../../../components/charts';
import { Box } from '../../../components/primitives/Box';
import { Card } from '../../../components/primitives/Card';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useCountUp } from '../../../hooks/useCountUp';
import { colors } from '../../../theme/colors';
import { darkPrecisionFontFamilies } from '../../../theme/typography';

type MetricType = 'revenue' | 'visitors' | 'conversion' | 'campaigns';

const metricColors: Record<MetricType, { border: string; glow: string; sparkline: string }> = {
  revenue: {
    border: colors.metrics.revenue,
    glow: colors.glow.green,
    sparkline: colors.metrics.revenue,
  },
  visitors: {
    border: colors.metrics.visitors,
    glow: colors.glow.cyan,
    sparkline: colors.metrics.visitors,
  },
  conversion: {
    border: colors.metrics.conversion,
    glow: colors.glow.purple,
    sparkline: colors.metrics.conversion,
  },
  campaigns: {
    border: colors.metrics.campaigns,
    glow: colors.glow.amber,
    sparkline: colors.metrics.campaigns,
  },
};

interface AnalyticsMetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  trend?: { value: number; direction: 'up' | 'down' };
  sparklineData?: number[];
  metricType: MetricType;
  index: number;
}

function parseMetricValue(value: string): {
  numeric: number;
  prefix: string;
  suffix: string;
  decimals: number;
  isNumeric: boolean;
} {
  const match = /^(\D*?)([\d,.]+)(.*)$/.exec(value);
  if (!match) return { numeric: 0, prefix: '', suffix: value, decimals: 0, isNumeric: false };
  const [, prefix, numStr, suffix] = match;
  const cleanNum = numStr.replaceAll(',', '');
  const dotIndex = cleanNum.indexOf('.');
  const decimals = dotIndex >= 0 ? cleanNum.length - dotIndex - 1 : 0;
  return { numeric: Number.parseFloat(cleanNum), prefix, suffix, decimals, isNumeric: true };
}

export function AnalyticsMetricCard({
  title,
  value,
  subtitle,
  trend,
  sparklineData,
  metricType,
  index,
}: Readonly<AnalyticsMetricCardProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const colorConfig = metricColors[metricType];
  const parsed = useMemo(() => parseMetricValue(value), [value]);
  const animatedNumber = useCountUp(parsed.numeric, 1200);
  // A placeholder like the em dash has no number to count up to: render it as-is.
  const displayValue = parsed.isNumeric
    ? `${parsed.prefix}${animatedNumber.toFixed(parsed.decimals).replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',')}${parsed.suffix}`
    : value;

  const webHoverStyle = Platform.select({
    web: {
      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer' as const,
      ...(isHovered && {
        boxShadow: `0 8px 24px ${colorConfig.glow}`,
        transform: [{ translateY: -3 }],
      }),
    },
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  const cardStyle: ViewStyle = {
    width: '100%',
    alignSelf: 'stretch',
    height: 160,
    ...(isHovered ? { borderColor: colorConfig.border } : null),
  };

  return (
    <AnimatedCard delay={index * 80} enablePress style={{ alignSelf: 'stretch', width: '100%' }}>
      <Card
        variant="default"
        padding="xl"
        radius="xl"
        style={[cardStyle, webHoverStyle]}
        {...hoverProps}
      >
        <VStack spacing="md" style={{ flex: 1, justifyContent: 'space-between' }}>
          <VStack spacing="md">
            <HStack justify="between" align="start">
              <Text
                variant="caption"
                color={colors.text.tertiary}
                style={{
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: '600',
                  fontSize: 11,
                }}
              >
                {title}
              </Text>
              {sparklineData && sparklineData.length > 1 && (
                <Sparkline
                  data={sparklineData}
                  width={80}
                  height={28}
                  color={colorConfig.sparkline}
                  showGradient
                  gradientOpacity={0.3}
                />
              )}
            </HStack>
            <Text
              color={colors.text.primary}
              style={{
                fontFamily: darkPrecisionFontFamilies.metricBold,
                fontSize: 30,
                lineHeight: 34,
                fontWeight: '600',
                letterSpacing: -1,
              }}
            >
              {displayValue}
            </Text>
          </VStack>
          <HStack justify="between" align="center">
            {subtitle ? (
              <Text variant="caption" color={colors.text.tertiary} style={{ fontSize: 12 }}>
                {subtitle}
              </Text>
            ) : (
              <Box />
            )}
            {trend && (
              <Box
                backgroundColor={
                  trend.direction === 'up' ? colors.status.success.bg : colors.status.error.bg
                }
                borderRadius="full"
                paddingX="sm"
                paddingY="xs"
              >
                <Text
                  variant="caption"
                  color={
                    trend.direction === 'up' ? colors.status.success.main : colors.status.error.main
                  }
                  style={{
                    fontFamily: darkPrecisionFontFamilies.metric,
                    fontWeight: '600',
                    fontSize: 12,
                  }}
                >
                  {trend.direction === 'up' ? '\u2191' : '\u2193'} {Math.abs(trend.value)}%
                </Text>
              </Box>
            )}
          </HStack>
        </VStack>
      </Card>
    </AnimatedCard>
  );
}

export type { MetricType };
